import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { gql, GraphQLError } from "../lib/api";
import {
  LIST_APPLICATIONS_QUERY,
  ON_APPLICATION_STATUS_CHANGED_SUBSCRIPTION,
} from "../lib/operations";
import { useAppSyncSubscription } from "../lib/subscription";
import { displayBusinessName } from "../lib/businessName";
import { Loader } from "../components/Loader";
import { ErrorMessage } from "../components/ErrorMessage";
import { StatusBadge } from "../components/StatusBadge";
import type { Application, ApplicationStatus } from "../lib/types";

/** First sentence of a multi-sentence error message. Looks for ". "
 *  or a newline; falls back to the first 200 chars if neither is found.
 *  Used for the live-updates failure banner so we surface the
 *  actionable bit without truncating mid-word. */
function firstSentence(s: string): string {
  const match = s.match(/^(.+?[.!?])\s/);
  if (match) return match[1];
  const nl = s.indexOf("\n");
  if (nl !== -1) return s.slice(0, nl);
  return s.length > 200 ? s.slice(0, 197) + "..." : s;
}

const STATUS_FILTERS: { value: ApplicationStatus | ""; label: string }[] = [
  { value: "", label: "All" },
  { value: "CREATED", label: "Created" },
  { value: "LINK_SENT", label: "Link Sent" },
  { value: "LINK_OPENED", label: "Link opened" },
  { value: "FORM_IN_PROGRESS", label: "In progress" },
  { value: "MNSP_SIGNED", label: "MNSP Signed" },
  { value: "READY_FOR_REVIEW", label: "Ready for review" },
  { value: "REVOKED", label: "Revoked" },
  { value: "SUBMITTED", label: "Submitted" },
  { value: "SIGNATURE_SENT", label: "Awaiting signature" },
  { value: "IN_REVIEW", label: "In review" },
  { value: "NEEDS_INFO", label: "Needs info" },
  { value: "APPROVED", label: "Approved" },
  { value: "BOARDED", label: "Boarded" },
  { value: "REJECTED", label: "Rejected" },
  { value: "ERROR", label: "Error" },
  { value: "ABANDONED", label: "Abandoned" },
  { value: "ARCHIVED", label: "Archived" },
];

// Search input debounce — keeps the filtered list from churning on
// every keystroke. 150ms is fast enough to feel instant on desktop
// while still skipping the intermediate states when an operator
// types a full business name in one go.
const SEARCH_DEBOUNCE_MS = 150;

export function Applications() {
  const [statusFilter, setStatusFilter] = useState<ApplicationStatus | "">("");
  // Two pieces of state: the live input value (what the user sees) and
  // the debounced query (what drives the filter). Splitting them lets
  // the input stay responsive while the expensive filter runs less often.
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [subscriptionDetailsOpen, setSubscriptionDetailsOpen] = useState(false);

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    gql<{
      listApplications: { applications: Application[]; nextToken?: string };
    }>(LIST_APPLICATIONS_QUERY, {
      status: statusFilter || null,
    })
      .then((data) => {
        if (cancelled) return;
        setApplications(
          data.listApplications.applications.sort((a, b) =>
            a.updatedAt > b.updatedAt ? -1 : 1,
          ),
        );
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(
          err instanceof GraphQLError
            ? err.message
            : err instanceof Error
              ? err.message
              : "Failed to load applications.",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [statusFilter]);

  // Subscribe to live status updates. When the poller (or anyone)
  // transitions an application's status via updateApplicationStatus,
  // the resolver returns the updated Application and AppSync fans it
  // out to all subscribed clients. We patch the matching row in place.
  const handleStatusEvent = useCallback(
    (payload: { onApplicationStatusChanged: Partial<Application> | null }) => {
      const updated = payload.onApplicationStatusChanged;
      if (!updated || !updated.applicationID) return;
      setApplications((prev) =>
        prev.map((a) =>
          a.applicationID === updated.applicationID ? { ...a, ...updated } : a,
        ),
      );
    },
    [],
  );
  const subscription = useAppSyncSubscription<{
    onApplicationStatusChanged: Partial<Application> | null;
  }>(ON_APPLICATION_STATUS_CHANGED_SUBSCRIPTION, {}, handleStatusEvent);

  const filtered = useMemo(() => {
    // ARCHIVED rows are hidden from the list unless the operator
    // explicitly filters to that status — otherwise they'd clutter
    // the default "All" scan, which is exactly what archiving them
    // was meant to avoid.
    const visible =
      statusFilter === "ARCHIVED"
        ? applications
        : applications.filter((a) => a.status !== "ARCHIVED");
    if (!search.trim()) return visible;
    const q = search.trim().toLowerCase();
    return visible.filter(
      (a) =>
        // Match either name so a legal-name search still finds the row
        // even though the list now displays the DBA.
        a.dbaName?.toLowerCase().includes(q) ||
        a.businessName?.toLowerCase().includes(q) ||
        a.merchantEmail?.toLowerCase().includes(q),
    );
  }, [applications, search, statusFilter]);

  return (
    <div>
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-brand-text">
            Applications
          </h1>
          <p className="mt-1 text-sm text-brand-muted">
            Merchant onboarding pipeline. Click an application to see status,
            audit log, and available actions.
          </p>
          {subscription.connected && (
            <p className="mt-1 text-xs text-emerald-400">
              <span className="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-emerald-400 align-middle" />
              Live updates connected
            </p>
          )}
          {subscription.error && (
            <div className="mt-1 text-xs text-amber-400">
              <span>
                {/* Render the first sentence (delimiter: ". ") so we surface
                    the actionable bit of the error without truncating
                    mid-word. The rest hides behind the Details disclosure. */}
                Live updates unavailable: {firstSentence(subscription.error)}
              </span>
              {subscription.error.length >
                firstSentence(subscription.error).length && (
                <>
                  {" "}
                  <button
                    type="button"
                    onClick={() => setSubscriptionDetailsOpen((v) => !v)}
                    className="underline-offset-2 hover:underline"
                  >
                    {subscriptionDetailsOpen ? "Hide details" : "Details"}
                  </button>
                  {subscriptionDetailsOpen && (
                    <pre className="mt-1 max-w-2xl overflow-x-auto whitespace-pre-wrap rounded bg-brand-bg p-2 text-amber-300/80">
                      {subscription.error}
                    </pre>
                  )}
                </>
              )}
            </div>
          )}
        </div>
        {/* createApplication is wired through to cardConnectStartFunction
            via the AppSync resolver added in commit 154c4e1. */}
        <Link
          to="/applications/new"
          className="btn-primary shrink-0 self-start py-2 sm:self-auto"
        >
          New application
        </Link>
      </div>

      <div className="mb-4 flex flex-wrap gap-3">
        <select
          value={statusFilter}
          onChange={(e) =>
            setStatusFilter(e.target.value as ApplicationStatus | "")
          }
          className="input max-w-xs"
          aria-label="Filter applications by status"
        >
          {STATUS_FILTERS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <input
          type="search"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search by business name or email"
          className="input max-w-sm"
          aria-label="Search applications by business name or email"
        />
      </div>

      {error && <ErrorMessage message={error} />}

      {loading ? (
        <div className="py-8">
          <Loader label="Loading applications..." />
        </div>
      ) : filtered.length === 0 ? (
        <div className="card p-8 text-center">
          <p className="text-brand-muted">
            {applications.length === 0
              ? "No applications yet. Start one to populate this list."
              : "No applications match your filters."}
          </p>
          {applications.length > 0 &&
            (statusFilter !== "" || searchInput.trim() !== "") && (
              <button
                type="button"
                onClick={() => {
                  setStatusFilter("");
                  setSearchInput("");
                }}
                className="btn-secondary mt-4 py-2"
              >
                Clear filters
              </button>
            )}
        </div>
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full min-w-[720px] text-left">
            <thead className="border-b border-brand-border bg-brand-bg text-xs uppercase tracking-wide text-brand-muted">
              <tr>
                <th className="px-4 py-3">Business</th>
                <th className="px-4 py-3">Merchant email</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Updated</th>
                <th className="px-4 py-3 text-right">MID</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => (
                <tr
                  key={a.applicationID}
                  className="border-b border-brand-border last:border-0 hover:bg-brand-bg/50"
                >
                  <td className="px-4 py-3">
                    <Link
                      to={`/applications/${a.applicationID}`}
                      className="text-brand-text underline-offset-2 hover:text-brand-blue hover:underline"
                    >
                      {displayBusinessName(a) || "—"}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-sm text-brand-muted">
                    {a.merchantEmail}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <StatusBadge status={a.status} />
                      {/* Send-form intake rows get a compact progress
                          indicator next to the status badge — gives the
                          operator at-a-glance "how far did the recipient
                          get" without needing to open the detail page. */}
                      {(a.status === "LINK_SENT" ||
                        a.status === "LINK_OPENED" ||
                        a.status === "FORM_IN_PROGRESS" ||
                        a.status === "REVOKED" ||
                        a.status === "READY_FOR_REVIEW") &&
                        a.intakeProgressPct != null && (
                          <span
                            className="flex items-center gap-1.5 text-xs text-brand-muted"
                            title={`${a.intakeProgressPct}% complete${
                              a.intakeOpensCount != null
                                ? `, ${a.intakeOpensCount} opens`
                                : ""
                            }`}
                          >
                            <span className="block h-1.5 w-16 overflow-hidden rounded-full bg-brand-border">
                              <span
                                className="block h-full bg-brand-blue"
                                style={{
                                  width: `${Math.max(2, a.intakeProgressPct)}%`,
                                }}
                              />
                            </span>
                            <span>{a.intakeProgressPct}%</span>
                          </span>
                        )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-brand-muted">
                    {a.updatedAt ? new Date(a.updatedAt).toLocaleString() : "—"}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-brand-muted">
                    {a.frontEndMid ?? "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
